import { useCallback, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { Tabs, Typography } from '@tempmee/react';
import {
  BackButton,
  Box,
  Column,
  FloatingContainer,
  InputSelect,
  Row,
  Screen,
} from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType } from '~/domain/DashboardJobs';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { fireShiftsOverviewActionNeededTabClicked } from '~/posthogs/events';
import { OpenTab, PendingTab, ScheduledTab } from './components';
import { FilterOptions } from './Constants';
import { TABS, TShiftListTab, useShiftList } from './hooks';
import { TJobListPageRoutes, TJobListPageRoutesLocationState } from './Types';

export const JobList = () => {
  const params = useParams<TJobListPageRoutes>();
  const location = useLocation() as Partial<{ state: Partial<TJobListPageRoutesLocationState> }>;

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  const [filterJobType, setFilterJobType] = useState<DashboardJobsType>(DashboardJobsType.ALL);

  const { activeTab, dashboardInfo, onChangeTab } = useShiftList({
    type: (location.state?.type || params.type) as DashboardJobsStatus,
    jobType: filterJobType,
  });

  const countOpen = dashboardInfo?.openCount || 0;
  const countPending = dashboardInfo?.pendingCount || 0;
  const countScheduled = dashboardInfo?.filledCount || 0;

  const handleTabChanged = useCallback(
    (tab: string) => {
      if (tab === TABS.PENDING) {
        fireShiftsOverviewActionNeededTabClicked();
      }
      onChangeTab(tab as TShiftListTab);
    },
    [onChangeTab],
  );

  return (
    <Screen>
      <FloatingContainer borderRadius={0} paddingBlock={48} paddingInline={16} height="100%">
        <Row position="relative" alignItems="center" justifyContent="center">
          <Box position="absolute" left={0}>
            <BackButton />
          </Box>
          <Typography as="span" bold size="heading_2" textAlign="center">
            {isEnforcePermanentJobV2 ? 'Shifts & Interviews' : 'Shifts Overview'}
          </Typography>
        </Row>
        <Row
          alignItems="center"
          justifyContent="space-between"
          marginBottom={36}
          marginInline={38}
          marginTop={24}
        >
          <Tabs defaultValue={TABS.OPEN} value={activeTab} onValueChange={handleTabChanged}>
            <Tabs.List style={{ marginBottom: 0 }}>
              <Tabs.Tab
                value={TABS.OPEN}
                label={countOpen > 0 ? (countOpen > 99 ? '99+' : String(countOpen)) : undefined}
                data-cy="open-shifts-tab"
              >
                Open
              </Tabs.Tab>
              <Tabs.Tab
                value={TABS.PENDING}
                label={
                  countPending > 0
                    ? countPending > 99
                      ? '99+'
                      : String(countPending)
                    : undefined
                }
                labelVariant="error"
                data-cy="action-needed-tab"
              >
                Action Needed
              </Tabs.Tab>
              <Tabs.Tab
                value={TABS.SCHEDULED}
                label={
                  countScheduled > 0
                    ? countScheduled > 99
                      ? '99+'
                      : String(countScheduled)
                    : undefined
                }
                data-cy="scheduled-shifts-tab"
              >
                Scheduled
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
          {isEnforcePermanentJobV2 && (
            <Row alignItems="center" gap={10}>
              <Typography as="span" bold size="body_large">
                Filter by:
              </Typography>
              <Box width={212}>
                <InputSelect
                  isSelectVisible={false}
                  options={FilterOptions}
                  value={filterJobType}
                  setValue={(val) => setFilterJobType(val as DashboardJobsType)}
                />
              </Box>
            </Row>
          )}
        </Row>
        <Column marginInline={36}>
          {activeTab === TABS.OPEN && <OpenTab jobType={filterJobType} />}
          {activeTab === TABS.PENDING && <PendingTab jobType={filterJobType} />}
          {activeTab === TABS.SCHEDULED && <ScheduledTab jobType={filterJobType} />}
        </Column>
      </FloatingContainer>
    </Screen>
  );
};
